import React from "react";
import { compose, withHooks, withStores } from "enhancers";
import { gql } from "@apollo/client";
import paths from "routes/paths";

import appStore from "stores/appStore";

import { Redirect, Route, Switch } from "components";
import { BlankLayout, MenuLayout } from "layouts";

// import SetupPasswordPage from "pages/auth/SetupPassword";
import ResetPasswordPage from "pages/auth/ResetPassword";
// import LoginPageComponent from "pages/main/authentication/LoginPage";

import AdminEditPage from "pages/main/admins/edit";
import AdminIndexPage from "pages/main/admins/index";
import AdminNewPage from "pages/main/admins/new";

import RoleIndexPage from "pages/main/roles/index";
import RoleNewPage from "pages/main/roles/new";
import RoleEditPage from "pages/main/roles/edit";

import SettingsIndexPage from "pages/main/settings";

import LanguagesDropdown from "components/common/LanguagesDropdown";
import { LoginPage } from "pages/main/authentication/LoginPage";
import { getRefreshToken, getToken } from "api";
import { InsuranceDetailPage } from "pages/main/insurance/detail";
import { InsuranceFormPage } from "pages/main/insurance/InsuranceForm";

import { InsuranceLandingPage } from "pages/main/insurance";
import InsurancePackages from "pages/main/insurance/insurancePackages";
import InsurancePackagesNewPage from "pages/main/insurance/insurancePackages/new";
import InsurancePackagesEditPage from "pages/main/insurance/insurancePackages/edit";
import ViewInsurancePage from "pages/main/insurance/insurancePackages/ViewInsurance";
import { PERMISSIONS } from "constants/enums/permissions";
import { EnrollmentPage } from "pages/main/enrollment";
import { ReportPage } from "pages/main/report";
import { EnrollmentDetailPage } from "pages/main/enrollment/detail";
import { EnrollmentInsurancePage } from "pages/main/enrollment/enrollmentInsurance";
import { InsuranceDetail } from "pages/main/insurance/InsuranceDetail";

// prettier-ignore
const InitialPages = () => (
  <BlankLayout>
    <LanguagesDropdown />
    <Switch>
      <Route path={paths.signUpPath()} exact component={LoginPage} />
      {/* <Route path={paths.setupPasswordPath()}   exact component={SetupPasswordPage} /> */}

      <Redirect to={paths.signUpPath()} />
    </Switch>
  </BlankLayout>
);

// prettier-ignore
const GuestPages = () => (
  <BlankLayout>
    <LanguagesDropdown />
    <Switch>
      <Route path={paths.signInPath()}         exact component={LoginPage}        />
      {/* <Route path={paths.setupPasswordPath()}  exact component={SetupPasswordPage} /> */}
      {/* <Route path={paths.resetPasswordPath()}  exact component={ResetPasswordPage} /> */}

      <Redirect to={paths.signInPath()} />
    </Switch>
  </BlankLayout>
);

// prettier-ignore
const MainPages = (props: any) => (
  <Switch>
    <Route
      path={paths.adminNewPath()}
      exact
      layout={MenuLayout}
      component={AdminNewPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.adminsPath()}
      exact
      layout={MenuLayout}
      component={AdminIndexPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.adminEditPath(":id")}
      exact
      layout={MenuLayout}
      component={AdminEditPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.rolesPath()}
      exact
      layout={MenuLayout}
      component={RoleIndexPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.roleNewPath()}
      exact
      layout={MenuLayout}
      component={RoleNewPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.roleEditPath(":id")}
      exact
      layout={MenuLayout}
      component={RoleEditPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.settingsPath()}
      exact
      layout={MenuLayout}
      component={SettingsIndexPage}
      permittedRoles={["SETTING_MANAGEMENT_READ"]}
    />

    <Route
      path={paths.insurancePackageNewPath(":insuranceId")}
      exact
      layout={MenuLayout}
      component={InsurancePackagesNewPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path={paths.insurancePackageEditPath(":insuranceId", ":id")}
      exact
      layout={MenuLayout}
      component={InsurancePackagesEditPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path={paths.insurancePackageViewInsurancePath(":insuranceId", ":id")}
      exact
      layout={MenuLayout}
      component={ViewInsurancePage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path={paths.addInsurancePath(":id")}
      exact
      layout={MenuLayout}
      component={InsuranceFormPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path={paths.editInsurancePath(":id", ":insuranceId")}
      exact
      layout={MenuLayout}
      component={InsuranceFormPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path={paths.insuranceDetailPath(":id")}
      layout={MenuLayout}
      component={InsuranceDetailPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path={paths.insurancePath()}
      exact
      layout={MenuLayout}
      component={InsuranceLandingPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />

    <Route
      path={paths.enrollmentPath()}
      exact
      layout={MenuLayout}
      component={EnrollmentPage}
      permittedRoles={[PERMISSIONS.ENROLLMENT_MANAGEMENT_READ]}
    />
    <Route
      path={paths.enrollmentDetailPath(":id")}
      layout={MenuLayout}
      component={EnrollmentDetailPage}
      permittedRoles={[PERMISSIONS.ENROLLMENT_MANAGEMENT_READ]}
    />
    <Route
      path={paths.enrollmentInsurancePath(":id", ":insuranceId")}
      exact
      layout={MenuLayout}
      component={EnrollmentInsurancePage}
      permittedRoles={[PERMISSIONS.ENROLLMENT_MANAGEMENT_READ]}
    />
    <Route
      path={paths.reportPath()}
      exact
      layout={MenuLayout}
      component={ReportPage}
      permittedRoles={[PERMISSIONS.REPORT_MANAGEMENT_READ]}
    />
    <Redirect withPermissions to={paths.enrollmentPath()} />
  </Switch>
);

interface RoutesProps {
  initialized: boolean;
  hasFirstAdmin: boolean;
  isAuthorized: boolean;
  isAdmin: boolean;
}

// prettier-ignore
const Routes = (props: RoutesProps) => {
  // return <MainPages />;
  if (!props.initialized) {
    return null;
  } else if (!props.hasFirstAdmin) {
    return <InitialPages />;
  } else if (!props.isAuthorized) {
    return <GuestPages />;
  } else {
    return <MainPages />;
  }
};

export const API = {
  GET_APP_INFO: gql`
    query GET_APP_INFO {
      info {
        hasFirstAdmin
      }
    }
  `,
  GET_CURRENT_USER: gql`
    query GET_CURRENT_USER {
      getCurrentBackofficeUser {
        id
        firstName
        lastName
        ownerType
        email
        admin {
          id
          code
          firstName
          lastName
        }
        role {
          title
          permissions
        }
      }
    }
  `,
};

const enhancer = compose(
  withStores((stores: { appStore: { currentUser: any } }) => ({
    currentUser: stores.appStore.currentUser,
  })),
  withHooks((props: any, hooks: any) => {
    const { currentUser } = props;
    const { useQuery, useMemo, useEffect, useLazyQuery, useState } = hooks;
    const [isSetCurrentUser, setIsSetCurrentUser] = useState(false);

    const appInfo = useQuery(API.GET_APP_INFO);
    const [fetchCurrentUser, newCurrentUserResponse] = useLazyQuery(
      API.GET_CURRENT_USER,
      {
        onCompleted: (data: any) => {
          appStore.setCurrentUser(data.getCurrentBackofficeUser);
          setIsSetCurrentUser(true);
        },
      }
    );

    const token = getToken();
    const refreshToken = getRefreshToken();

    useEffect(() => {
      if (token && refreshToken) {
        fetchCurrentUser();
      }
    }, [fetchCurrentUser, token, refreshToken]);
    const { loading, data, error } = newCurrentUserResponse || {};

    const initialized =
      !appInfo.loading &&
      !newCurrentUserResponse.loading &&
      ((token && refreshToken && isSetCurrentUser) ||
        (!token && !refreshToken));
    const isAuthorized = currentUser && isSetCurrentUser;
    const hasFirstAdmin = !!appInfo.data?.info.hasFirstAdmin;

    const ownerType = useMemo(() => {
      if (loading || error) {
        return null;
      }
      return data?.getCurrentBackofficeUser?.ownerType;
    }, [loading, data, error]);

    useEffect(() => {
      const $splashScreen = document.getElementById("splash-screen");
      if ($splashScreen) {
        const display = initialized ? "none" : "";
        $splashScreen.style.display = display;
      }
    }, [initialized]);

    useEffect(() => {
      appStore.setHasFirstAdmin(hasFirstAdmin);
    }, [hasFirstAdmin]);

    const isAdmin = ownerType === "Admin";

    return {
      initialized,
      isAuthorized,
      hasFirstAdmin,
      isAdmin,
    };
  })
);

export default enhancer(Routes);
